import { get, isEmpty } from 'lodash';

import { TREND_LINE_DEFINITIONS, TREND_LINE_LABELS } from 'constants/dashboard';
import { getHeatmapColors, setColorGrowing } from 'helper/dashboardColors';
import { colors } from 'muiTheme/palette';

// region PILLAR EXPERIENCE TRACKER
export const formatPillarExperienceTracker = (pillarExperienceTracker) =>
  pillarExperienceTracker.map((item) => ({
    name: item.name,
    points: item.points.map((point) => ({
      name: point.date,
      pillars: point.avg_by_pillar.map((avg) => ({
        name: avg.pillar,
        value: avg.average || 0,
        color: avg.color,
      })),
    })),
  }));
// endregion

// region KEY EVENTS
export const formatKeyEventsData = (keyEventsData) =>
  !keyEventsData.length
    ? []
    : keyEventsData
        .toSorted((a, b) => +new Date(a.event_date) - +new Date(b.event_date))
        .map((item) => ({
          name: item.name,
          date: +new Date(item?.event_date),
          ...(item.msg
            ? {
                msg: item.msg,
              }
            : {
                avg_score: item?.average_rate,
                growing: item.growing,
                growing_color: setColorGrowing(item.growing),
                trend: item.trend_delta?.direction,
                least_affected: {
                  ...item.least_affected,
                  title: item.least_affected.pillar,
                  least_affected_color: setColorGrowing(item.least_affected.diff_percentage),
                },
                most_affected: {
                  ...item.most_affected,
                  title: item.most_affected.pillar,
                  diff_percentage_color: setColorGrowing(item.most_affected.diff_percentage),
                },
              }),
        }));
// endregion

// region HEATMAP
const getPillarInfo = (data, pillarId, field) =>
  get(
    data.find((pillarData) => pillarData.pillar_id === pillarId),
    field,
    null,
  );

export const formatHeatmapData = (heatmapData = {}) => {
  if (isEmpty(heatmapData)) {
    return [];
  }

  const {
    pillar_tracker_by_segment: pillarTrackerBySegment = {},
    trend: trendData = [],
    deviation: deviationData = [],
    average_by_pillar: averageByPillar = [],
  } = heatmapData;
  const groupQuestions = Object.keys(pillarTrackerBySegment);

  return groupQuestions.map((groupQuestion) => {
    const pillars = pillarTrackerBySegment[groupQuestion] ?? [];
    const pillarsIds = pillars.map((pillar) => pillar.pillar_id);
    const segmentsNames = (pillars[0]?.average_by_segment ?? []).map((segment) => segment.segment_title);

    const headers = [
      {
        id: 'header-pillars',
        name: 'Pillars',
        width: '25%',
        typographyProps: {
          variant: 'body2',
          textAlign: 'left',
        },
      },
      ...pillars.map(({ pillar: name, pillar_id: id, average_by_segment: averageBySegment = [], statement }) => ({
        name,
        statement,
        id: `header-${id}`,
        color: averageBySegment[0]?.color,
      })),
    ];

    const allTimeTrendRow = {
      id: 'trends-all-time',
      cells: [
        {
          id: 'trend-all-time-name',
          value: TREND_LINE_LABELS.ALL_TIME,
          isTextValue: true,
          tooltipText: TREND_LINE_DEFINITIONS.ALL_TIME,
          typographyProps: { variant: 'body2' },
        },
        ...pillarsIds.map((pillarId) => ({
          id: `trend-all-time-${pillarId}`,
          value: getPillarInfo(trendData, pillarId, 'tend_index'),
          isTrendValue: true,
        })),
      ],
    };

    const fromPreviousTrendRow = {
      id: 'trends-from-previous',
      cells: [
        {
          id: 'trend-from-previous-name',
          value: TREND_LINE_LABELS.FROM_PREVIOUS,
          isTextValue: true,
          tooltipText: TREND_LINE_DEFINITIONS.FROM_PREVIOUS,
          typographyProps: { variant: 'body2' },
        },
        ...pillarsIds.map((pillarId) => ({
          id: `trend-from-previous-${pillarId}`,
          value: getPillarInfo(trendData, pillarId, 'trend_delta.direction'),
          isTrendValue: true,
        })),
      ],
    };

    const deviationRow = {
      id: 'deviation',
      cells: [
        {
          id: 'deviation-name',
          value: 'Score Distribution',
          isTextValue: true,
          typographyProps: { variant: 'body2' },
        },
        ...pillarsIds.map((pillarId) => ({
          id: `deviation-${pillarId}`,
          value: getPillarInfo(deviationData, pillarId, 'deviation'),
          isDeviationValue: true,
        })),
      ],
    };

    const averageRow = {
      id: 'average',
      cells: [
        {
          id: 'average-name',
          value: 'Average score',
          isTextValue: true,
          typographyProps: { variant: 'body2' },
        },
        ...pillarsIds.map((pillarId) => ({
          id: `average-${pillarId}`,
          value: getPillarInfo(averageByPillar, pillarId, 'average'),
          isNumberValue: true,
          styles: { backgroundColor: colors.lightPale },
        })),
      ],
    };

    const segmentsRows = segmentsNames.map((segmentName, segmentIndex) => ({
      id: `segment-${segmentIndex}`,
      cells: [
        {
          id: 'segment-name',
          value: segmentName,
          isTextValue: true,
        },
        ...pillars.map(({ pillar_id: pillarId, average_by_segment: averageBySegment }) => {
          const value = averageBySegment[segmentIndex]?.average || 0;

          return {
            value,
            id: `segment-${segmentIndex}-${pillarId}`,
            isNumberValue: true,
            styles: {
              backgroundColor: getHeatmapColors(value),
              color: 'white',
            },
          };
        }),
      ],
    }));

    return {
      headers,
      rows: [fromPreviousTrendRow, allTimeTrendRow, deviationRow, averageRow, ...segmentsRows],
      groupQuestion,
    };
  });
};

export const formatMasterDashboardHeatmapData = (heatmapData = []) => {
  if (!heatmapData.length) {
    return heatmapData;
  }

  const groupQuestions = Object.keys(heatmapData[0].segments);
  const headers = [
    {
      id: 'header-pillars',
      name: 'Pillars',
      width: '25%',
      typographyProps: {
        variant: 'body2',
        textAlign: 'left',
      },
    },
  ];
  const allTimeTrendRow = {
    id: 'trends-all-time',
    cells: [
      {
        id: 'trend-all-time-name',
        value: TREND_LINE_LABELS.ALL_TIME,
        isTextValue: true,
        tooltipText: TREND_LINE_DEFINITIONS.ALL_TIME,
        typographyProps: { variant: 'body2' },
      },
    ],
  };
  const fromPreviousTrendRow = {
    id: 'trends-from-previous',
    cells: [
      {
        id: 'trend-from-previous-name',
        value: TREND_LINE_LABELS.FROM_PREVIOUS,
        isTextValue: true,
        tooltipText: TREND_LINE_DEFINITIONS.FROM_PREVIOUS,
        typographyProps: { variant: 'body2' },
      },
    ],
  };
  const deviationRow = {
    id: 'deviation',
    cells: [
      {
        id: 'deviation-name',
        value: 'Score Distribution',
        isTextValue: true,
        typographyProps: { variant: 'body2' },
      },
    ],
  };
  const averageRow = {
    id: 'average',
    cells: [
      {
        id: 'average-name',
        value: 'Average score',
        isTextValue: true,
        typographyProps: { variant: 'body2' },
      },
    ],
  };

  heatmapData.forEach(({ pillar, statement, average, color, deviation, trend, trend_delta: trendDelta }, index) => {
    headers.push({
      name: pillar,
      statement,
      color,
      id: `header-${index}`,
    });
    allTimeTrendRow.cells.push({
      id: `trend-all-time-${index}`,
      value: trend,
      isTrendValue: true,
    });
    fromPreviousTrendRow.cells.push({
      id: `trend-from-previous-${index}`,
      value: trendDelta?.direction,
      isTrendValue: true,
    });
    deviationRow.cells.push({
      id: `deviation-${index}`,
      value: deviation,
      isDeviationValue: true,
    });
    averageRow.cells.push({
      id: `average-${index}`,
      value: average,
      isNumberValue: true,
      styles: { backgroundColor: colors.lightPale },
    });
  });

  return groupQuestions.map((groupQuestion) => {
    const currentGroupSegments = heatmapData[0].segments[groupQuestion] || [];
    const segmentsNames = currentGroupSegments.map((segment) => segment.segment_title);
    const segmentsRows = segmentsNames.map((segmentName, segmentIndex) => ({
      id: `segment-${segmentIndex}`,
      cells: [
        {
          id: 'segment-name',
          value: segmentName,
          isTextValue: true,
        },
        ...heatmapData.map(({ segments }, index) => {
          const value = segments[groupQuestion][segmentIndex]?.average || 0;

          return {
            value,
            id: `segment-${segmentIndex}-${index}`,
            isNumberValue: true,
            styles: {
              backgroundColor: getHeatmapColors(value),
              color: 'white',
            },
          };
        }),
      ],
    }));

    return {
      headers,
      rows: [fromPreviousTrendRow, allTimeTrendRow, deviationRow, averageRow, ...segmentsRows],
      groupQuestion,
    };
  });
};
// endregion
